// @ts-expect-error - Used in JSX but TypeScript doesn't detect it
import { ArrowLeft, ChevronDown, ChevronUp, Search, X } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import HelpIcon from '../assets/Helpicon.svg'
import CompanyAdd from '../assets/homeplusimg.svg'
import AuthenticatedLayout from '../components/AuthenticatedLayout'
import {
  AddCompanyRequest,
  CompanyDetails,
  CompanySearchResult,
  StoredCompany,
} from '../types'
import { ApiError } from '../utils/api'
import { companyService } from '../utils/companyService'
import { useUserProfile } from '../context/UserProfileContext'
import { formatSicCodesList } from '../utils/sicCodes'
import { formatJurisdiction, formatUkDate } from '../utils/formatters'

type ViewState =
  | 'initial'
  | 'loading'
  | 'results'
  | 'no-results'
  | 'details'
  | 'adding'
  | 'success'
  | 'error'

export default function AddCompanyPage() {
  const [searchQuery, setSearchQuery] = useState('')
  const [viewState, setViewState] = useState<ViewState>('initial')
  const [selectedCompany, setSelectedCompany] = useState<string | null>(null)
  const [searchResults, setSearchResults] = useState<CompanySearchResult[]>([])
  const [companyDetails, setCompanyDetails] = useState<CompanyDetails | null>(
    null
  )
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  // Form fields state
  const [formData, setFormData] = useState({
    hmrcUsername: '',
    companyHouseAuthCode: '',
    hmrcPassword: '',
  })
  // @ts-expect-error - Used in JSX but TypeScript doesn't detect it
  const [showCredentialSection, setShowCredentialSection] = useState(false)
  const [isAdding, setIsAdding] = useState(false)
  useUserProfile() // Ensure context is initialized

  const navigate = useNavigate()

  const formatAddress = (
    address?:
      | CompanyDetails['registeredOffice']
      | CompanySearchResult['address']
  ) => {
    if (!address) return '—'

    const record = address as Record<string, unknown>
    const pickValue = (...keys: string[]) => {
      for (const key of keys) {
        const value = record[key]
        if (typeof value === 'string' && value.trim().length > 0) {
          return value
        }
      }
      return null
    }

    const line1 = pickValue('line1', 'addressLine1', 'premises')
    const line2 = pickValue('line2', 'addressLine2')
    const locality = pickValue('city', 'locality')
    const region = pickValue('county', 'region')
    const postcode = pickValue('postcode', 'postalCode')
    const country = pickValue('country')

    const parts = [line1, line2, locality, region, postcode, country]
    return parts.filter(Boolean).join(', ')
  }

  const sanitize = (value: string) => value.trim() || undefined

  const handleSearch = async (value: string) => {
    setSearchQuery(value)
    setError(null)
    setSuccess(null)

    if (value.length === 0) {
      setViewState('initial')
      setSearchResults([])
      return
    }

    if (value.length < 2) {
      return // Don't search for very short queries
    }

    setViewState('loading')

    try {
      const { statusCode, response } = await companyService.searchCompanies({
        q: value,
        items_per_page: 20,
        start_index: 0,
      })

      if (statusCode >= 400) {
        setError('Unable to search Companies House at the moment.')
        setSearchResults([])
        setViewState('no-results')
        return
      }

      const fallbackSuggestions = Array.isArray(
        (response as any)?.suggestions
      )
        ? ((response as any).suggestions as CompanySearchResult[])
        : []

      const suggestions =
        response?.data?.suggestions ?? fallbackSuggestions
      const isSuccess = response?.success ?? true

      if (!isSuccess) {
        setError(
          response?.message || 'Companies House search failed. Try again.'
        )
        setSearchResults([])
        setViewState('no-results')
        return
      }

      if (suggestions.length > 0) {
        setSearchResults(suggestions)
        setViewState('results')
      } else {
        setSearchResults([])
        setViewState('no-results')
      }
    } catch (err) {
      console.error('Search error:', err)
      if (err instanceof ApiError) {
        setError(err.message || 'Failed to search companies. Please try again.')
      } else {
        setError('Failed to search companies. Please try again.')
      }
      setViewState('no-results')
    }
  }

  const handleClearSearch = () => {
    setSearchQuery('')
    setViewState('initial')
    setSelectedCompany(null)
    setSearchResults([])
    setCompanyDetails(null)
    setError(null)
    setSuccess(null)
    setShowCredentialSection(false)
    setFormData({
      hmrcUsername: '',
      companyHouseAuthCode: '',
      hmrcPassword: '',
    })
  }

  const handleSelectCompany = async (companyNumber: string) => {
    setSelectedCompany(companyNumber)
    setViewState('loading')
    setError(null)
    setSuccess(null)

    try {
      const { statusCode, response } =
        await companyService.getCompanyDetails(companyNumber)

      if (statusCode >= 400) {
        setError('Unable to fetch company details. Please try again later.')
        setViewState('results')
        return
      }

      const isSuccess = response?.success ?? true
      const companyData =
        (response as { data?: CompanyDetails }).data ??
        (response as unknown as CompanyDetails)

      if (!isSuccess || !companyData) {
        setError(response?.message || 'Company details were not returned.')
        setViewState('results')
        return
      }
      setCompanyDetails(companyData)
      setShowCredentialSection(true)
      setViewState('details')
    } catch (err) {
      console.error('Company details error:', err)
      if (err instanceof ApiError) {
        setError(
          err.message || 'Failed to load company details. Please try again.'
        )
      } else {
        setError('Failed to load company details. Please try again.')
      }
      setViewState('results')
    }
  }

  const handleAddCompany = async () => {
    if (!companyDetails) return

    setIsAdding(true)
    setViewState('adding')
    setError(null)
    setSuccess(null)

    try {
      const payload: AddCompanyRequest = {
        ...companyDetails,
      }

      const hmrcUsername = sanitize(formData.hmrcUsername)
      const hmrcPassword = sanitize(formData.hmrcPassword)
      const companyHouseAuthCode = sanitize(formData.companyHouseAuthCode)

      if (hmrcUsername) {
        payload.hmrcUsername = hmrcUsername
      }
      if (hmrcPassword) {
        payload.hmrcPassword = hmrcPassword
      }
      if (companyHouseAuthCode) {
        payload.companyHouseAuthCode = companyHouseAuthCode
      }

      const { statusCode, response } =
        await companyService.addCompanyFromCompaniesHouse(payload)

      if (statusCode >= 400) {
        throw new ApiError('Failed to add company', statusCode)
      }

      const isSuccess = response?.success ?? true
      const addedCompany =
        (response as { data?: StoredCompany }).data ??
        (response as unknown as StoredCompany)

      if (!isSuccess || !addedCompany) {
        throw new ApiError(
          response?.message || 'Failed to add company.',
          statusCode
        )
      }

      setSuccess(
        `${
          addedCompany.companyName ||
          (addedCompany as StoredCompany & { name?: string }).name ||
          'Company'
        } added successfully!`
      )
      setIsAdding(false)
      setViewState('success')
      setShowCredentialSection(false)

      // Navigate to dashboard or company list after a short delay
      setTimeout(() => {
        navigate('/dashboard')
      }, 2000)
    } catch (err) {
      console.error('Add company error:', err)
      if (err instanceof ApiError) {
        setError(
          (err.response?.response?.message as string) ||
            err.message ||
            'Failed to add company. Please try again.'
        )
      } else if (err instanceof Error) {
        setError(err.message || 'Failed to add company. Please try again.')
      } else {
        setError('Failed to add company. Please try again.')
      }
      setIsAdding(false)
      setViewState('details')
    }
  }

  return (
    <AuthenticatedLayout activeTab='dashboard'>
      <div className='min-h-screen bg-[#FFFFFF] rounded-[15px]'>
        <div className='mx-auto xl:px-28 px-6 py-8'>
          {/* Header */}
          <div className='mb-8 flex items-center justify-between gap-3'>
            {/* Back Button */}
            <button
              onClick={() => navigate(-1)}
              className='flex h-[34px] w-[34px] items-center cursor-pointer justify-center rounded-full bg-gray-200 hover:bg-gray-300 transition-colors z-10 flex-shrink-0'
            >
              <ArrowLeft className='h-5 w-5 text-gray-700' />
            </button>
            <div className='flex flex-row items-center justify-center gap-2 sm:gap-4 min-w-0'>
              <img src={CompanyAdd} alt='CompanyAdd' className='hidden sm:block' />
              <h1 className='text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 truncate'>
                Add New Company
              </h1>
              <img src={HelpIcon} alt='Help Icons' className='flex-shrink-0' />
            </div>
            <div className='w-[34px] flex-shrink-0' />
          </div>

          {/* Description (only on initial state) */}
          {viewState === 'initial' && (
            <p className='mb-8 text-center text-sm leading-relaxed text-gray-600 max-w-4xl mx-auto'>
              Search Companies House to locate the company you need to file for.
              Once selected we will import the statutory profile, capture filing
              permissions, and prepare the record for CT600 and HMRC
              integrations.
            </p>
          )}

          {/* Search Input */}
          <div className='mb-8 mx-auto max-w-3xl'>
            <div className='relative'>
              <Search className='absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400' />
              <input
                type='text'
                value={searchQuery}
                onChange={e => handleSearch(e.target.value)}
                placeholder='Search with company name or registration number'
                className='w-full rounded-lg border border-gray-300 bg-white py-4 pl-12 pr-12 text-base text-gray-900 placeholder:text-gray-400 focus:border-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-400'
              />
              {searchQuery && (
                <button
                  onClick={handleClearSearch}
                  className='absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600'
                >
                  <X className='h-5 w-5' />
                </button>
              )}
            </div>
          </div>

          {/* Loading State */}
          {viewState === 'loading' && (
            <div className='mx-auto max-w-3xl'>
              <h2 className='mb-4 text-lg font-semibold text-gray-900'>
                Search results
              </h2>
              <div className='space-y-3'>
                {[1, 2, 3, 4].map(i => (
                  <div
                    key={i}
                    className='flex items-center gap-4 rounded-lg bg-white p-4'
                  >
                    <div className='h-5 w-5 animate-pulse rounded-full bg-gray-200' />
                    <div className='h-4 flex-1 animate-pulse rounded bg-gray-200' />
                    <div className='h-4 w-20 animate-pulse rounded bg-gray-200' />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Search Results */}
          {viewState === 'results' && (
            <div className='mx-auto max-w-3xl'>
              <h2 className='mb-4 text-lg font-semibold text-gray-900'>
                Search results
              </h2>
              {error && (
                <div className='mb-4 rounded-lg bg-red-50 p-4 text-sm text-red-600'>
                  {error}
                </div>
              )}
              <div className='space-y-2'>
                {searchResults.map(company => {
                  const addressSummary = formatAddress(company.address)
                  return (
                    <button
                      key={company.companyNumber}
                      onClick={() => handleSelectCompany(company.companyNumber)}
                      className={`flex w-full items-center gap-4 rounded-lg p-4 text-left transition-colors ${
                        selectedCompany === company.companyNumber
                          ? 'bg-gray-100'
                          : 'bg-white hover:bg-gray-50'
                      }`}
                    >
                      <Search className='h-5 w-5 text-gray-400' />
                      <div className='flex-1'>
                        <div className='text-base font-medium text-gray-900'>
                          {company.companyName}
                        </div>
                        <div className='text-sm text-gray-500'>
                          {company.companyNumber} • {company.companyStatus}
                        </div>
                        {addressSummary !== '—' && (
                          <div className='text-sm text-gray-500'>
                            {addressSummary}
                          </div>
                        )}
                        {company.incorporationDate && (
                          <div className='text-xs text-gray-400 mt-1'>
                            Incorporated {formatUkDate(company.incorporationDate)}
                          </div>
                        )}
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {/* No Results State */}
          {viewState === 'no-results' && (
            <div className='mx-auto max-w-3xl text-center'>
              <div className='mb-8 flex justify-center'>
                <div className='relative'>
                  <svg
                    width='120'
                    height='120'
                    viewBox='0 0 120 120'
                    fill='none'
                  >
                    {/* Document */}
                    <rect
                      x='30'
                      y='20'
                      width='50'
                      height='70'
                      rx='4'
                      fill='#E5E7EB'
                    />
                    <rect
                      x='38'
                      y='32'
                      width='34'
                      height='4'
                      rx='2'
                      fill='#D1D5DB'
                    />
                    <rect
                      x='38'
                      y='42'
                      width='28'
                      height='4'
                      rx='2'
                      fill='#D1D5DB'
                    />
                    <rect
                      x='38'
                      y='52'
                      width='34'
                      height='4'
                      rx='2'
                      fill='#D1D5DB'
                    />
                    <rect
                      x='38'
                      y='62'
                      width='24'
                      height='4'
                      rx='2'
                      fill='#D1D5DB'
                    />

                    {/* Magnifying Glass */}
                    <circle cx='75' cy='75' r='15' fill='#EF4444' />
                    <circle cx='75' cy='75' r='10' fill='white' />
                    <rect
                      x='85'
                      y='85'
                      width='4'
                      height='15'
                      rx='2'
                      fill='#EF4444'
                      transform='rotate(45 85 85)'
                    />

                    {/* Sparkles */}
                    <circle cx='50' cy='15' r='2' fill='#D1D5DB' />
                    <circle cx='90' cy='25' r='2' fill='#D1D5DB' />
                    <circle cx='25' cy='50' r='2' fill='#D1D5DB' />
                    <circle cx='95' cy='60' r='2' fill='#D1D5DB' />
                  </svg>
                </div>
              </div>
              <h2 className='mb-4 text-xl font-semibold text-gray-900'>
                No Result Found
              </h2>
              <ul className='space-y-2 text-sm text-gray-600'>
                <li>Check spelling</li>
                <li>Try searching with the company registered number</li>
              </ul>
            </div>
          )}

          {/* Company Details Form */}
          {viewState === 'details' && companyDetails && (
            <div className='mx-auto max-w-3xl'>
              <div className='rounded-lg bg-[#F5F5F5] p-8 shadow-sm'>
                {/* Success Message */}
                {success && (
                  <div className='mb-6 rounded-lg bg-green-50 p-4 text-sm text-green-600'>
                    {success}
                  </div>
                )}

                {/* Error Message */}
                {error && (
                  <div className='mb-6 rounded-lg bg-red-50 p-4 text-sm text-red-600'>
                    {error}
                  </div>
                )}

                {/* Companies House Header */}
                <div className='mb-6 flex items-center gap-3 border-b pb-4'>
                  <div className='flex h-10 w-10 items-center justify-center rounded bg-gray-900'>
                    <div className='text-xs font-bold text-white'>CH</div>
                  </div>
                  <span className='text-base font-semibold text-gray-900'>
                    Companies House
                  </span>
                </div>

                {/* Company Information Grid */}
                <div className='mb-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 rounded-lg bg-gray-50 p-4 sm:p-6'>
                  <div>
                    <span className='text-sm text-gray-600'>
                      Company Name:{' '}
                    </span>
                    <span className='text-sm font-semibold text-gray-900'>
                      {companyDetails.companyName}
                    </span>
                  </div>
                  <div>
                    <span className='text-sm text-gray-600'>
                      Company Type:{' '}
                    </span>
                    <span className='text-sm font-semibold text-gray-900'>
                      {formatJurisdiction(companyDetails.jurisdiction) || 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className='text-sm text-gray-600'>Company No: </span>
                    <span className='text-sm font-semibold text-gray-900'>
                      {companyDetails.companyNumber}
                    </span>
                  </div>
                  <div>
                    <span className='text-sm text-gray-600'>SIC Codes: </span>
                    {companyDetails.sicCodes && companyDetails.sicCodes.length > 0 ? (
                      <div className='mt-1 space-y-1'>
                        {formatSicCodesList(companyDetails.sicCodes).map((formattedCode, index) => (
                          <div key={index} className='text-sm font-semibold text-gray-900'>
                            {formattedCode}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span className='text-sm font-semibold text-gray-900'>N/A</span>
                    )}
                  </div>
                  <div>
                    <span className='text-sm text-gray-600'>Status: </span>
                    <span
                      className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${
                        companyDetails.companyStatus.toLowerCase() === 'active'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-red-100 text-red-700'
                      }`}
                    >
                      {companyDetails.companyStatus}
                    </span>
                  </div>
                  <div>
                    <span className='text-sm text-gray-600'>
                      Next Due Date:{' '}
                    </span>
                    <span className='text-sm font-semibold text-gray-900'>
                      {formatUkDate(companyDetails.accountingPeriod?.nextDue) || 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className='text-sm text-gray-600'>Address: </span>
                    <span className='text-sm font-semibold text-gray-900'>
                      {formatAddress(companyDetails.registeredOffice)}
                    </span>
                  </div>
                  <div>
                    <span className='text-sm text-gray-600'>
                      Last Made Up To:{' '}
                    </span>
                    <span className='text-sm font-semibold text-gray-900'>
                      {formatUkDate(companyDetails.accountingPeriod?.lastMadeUpTo) || 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className='text-sm text-gray-600'>
                      Incorporation:{' '}
                    </span>
                    <span className='text-sm font-semibold text-gray-900'>
                      {formatUkDate(companyDetails.incorporationDate) || 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className='text-sm text-gray-600'>
                      Next Made Up To:{' '}
                    </span>
                    <span className='text-sm font-semibold text-gray-900'>
                      {formatUkDate(companyDetails.accountingPeriod?.nextMadeUpTo) || 'N/A'}
                    </span>
                  </div>
                  <div className='sm:col-span-2 flex flex-wrap gap-x-4 gap-y-2'>
                    <span className='text-sm'>
                      <span className='text-gray-600'>Can File: </span>
                      <span className='font-semibold text-gray-900'>
                        {companyDetails.canFile ? 'Yes' : 'No'}
                      </span>
                    </span>
                    <span className='text-sm'>
                      <span className='text-gray-600'>Has Charges: </span>
                      <span className='font-semibold text-gray-900'>
                        {companyDetails.hasCharges ? 'Yes' : 'No'}
                      </span>
                    </span>
                    <span className='text-sm'>
                      <span className='text-gray-600'>Insolvency History: </span>
                      <span className='font-semibold text-gray-900'>
                        {companyDetails.hasInsolvencyHistory ? 'Yes' : 'No'}
                      </span>
                    </span>
                    <span className='text-sm'>
                      <span className='text-gray-600'>Registered Office Deliverable: </span>
                      <span className='font-semibold text-gray-900'>
                        {companyDetails.undeliverableRegisteredOfficeAddress
                          ? 'No'
                          : 'Yes'}
                      </span>
                    </span>
                  </div>
                </div>

                {/* Form Fields */}
                <div className='space-y-6'>
                  {/* <div className='rounded-lg border border-gray-200 bg-white/70 p-4'>
                    <button
                      type='button'
                      onClick={() =>
                        setShowCredentialSection(prev => !prev)
                      }
                      className='flex w-full items-center justify-between text-left'
                    >
                      <div>
                        <h3 className='text-sm font-semibold text-gray-800'>
                          Optional filing credentials
                        </h3>
                        <p className='text-xs text-gray-500'>
                          Store gateway login details to streamline HMRC
                          authorisation later.
                        </p>
                      </div>
                      {showCredentialSection ? (
                        <ChevronUp className='h-4 w-4 text-gray-500' />
                      ) : (
                        <ChevronDown className='h-4 w-4 text-gray-500' />
                      )}
                    </button>

                    {showCredentialSection && (
                      <div className='mt-4 grid grid-cols-1 gap-4 md:grid-cols-2'>
                        <div>
                          <label htmlFor='hmrc-username' className='mb-1 block text-xs font-medium text-gray-600'>
                            HMRC Gateway Username
                          </label>
                          <input
                            id='hmrc-username'
                            type='text'
                            value={formData.hmrcUsername}
                            onChange={e =>
                              handleFormChange('hmrcUsername', e.target.value)
                            }
                            className='w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-400'
                          />
                        </div>
                        <div>
                          <label htmlFor='hmrc-password' className='mb-1 block text-xs font-medium text-gray-600'>
                            HMRC Gateway Password
                          </label>
                          <input
                            id='hmrc-password'
                            type='password'
                            value={formData.hmrcPassword}
                            onChange={e =>
                              handleFormChange('hmrcPassword', e.target.value)
                            }
                            className='w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-400'
                          />
                        </div>
                        <div>
                          <label htmlFor='companies-house-auth-code' className='mb-1 block text-xs font-medium text-gray-600'>
                            Companies House Auth Code
                          </label>
                          <input
                            id='companies-house-auth-code'
                            type='text'
                            value={formData.companyHouseAuthCode}
                            onChange={e =>
                              handleFormChange(
                                'companyHouseAuthCode',
                                e.target.value
                              )
                            }
                            className='w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-400'
                          />
                        </div>
                      </div>
                    )}
                  </div> */}

                  {/* Submit Button */}
                  <div className='mt-8 flex justify-center'>
                    <button
                      onClick={handleAddCompany}
                      disabled={isAdding}
                      className='rounded-lg bg-gray-900 px-12 py-3 text-base font-medium text-white hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed'
                    >
                      {isAdding ? 'Adding Company...' : 'Add Company'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Adding State */}
          {viewState === 'adding' && (
            <div className='mx-auto max-w-3xl text-center'>
              <div className='mb-8 flex justify-center'>
                <div className='h-12 w-12 animate-spin rounded-full border-4 border-gray-200 border-t-gray-900'></div>
              </div>
              <h2 className='mb-4 text-xl font-semibold text-gray-900'>
                Adding Company
              </h2>
              <p className='text-sm text-gray-600'>
                Please wait while we add your company to the system...
              </p>
            </div>
          )}

          {/* Success State */}
          {viewState === 'success' && (
            <div className='mx-auto max-w-3xl text-center'>
              <div className='mb-8 flex justify-center'>
                <div className='flex h-16 w-16 items-center justify-center rounded-full bg-green-100'>
                  <span className='text-2xl text-green-600'>✓</span>
                </div>
              </div>
              <h2 className='mb-4 text-xl font-semibold text-gray-900'>
                Company Added Successfully!
              </h2>
              <p className='mb-8 text-sm text-gray-600'>
                Your company has been successfully added to the system.
              </p>
              <div className='flex justify-center gap-4'>
                <button
                  onClick={() => navigate('/dashboard')}
                  className='rounded-lg bg-gray-900 px-6 py-3 text-sm font-medium text-white hover:bg-gray-800'
                >
                  Go to Dashboard
                </button>
                <button
                  onClick={() => {
                    setViewState('initial')
                    setSearchQuery('')
                    setSelectedCompany(null)
                    setCompanyDetails(null)
                    setError(null)
                    setSuccess(null)
                  }}
                  className='rounded-lg border border-gray-300 px-6 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50'
                >
                  Add Another Company
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

    </AuthenticatedLayout>
  )
}
